/**
 * Chat-history REST API on the copilot service (ai-pacs-copilot `/api`).
 * Conversations are auto-created by the WS consumer; this only lists / loads /
 * renames / deletes them. Auth uses the same doctor token as pacs-be calls.
 */
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { config } from 'services/config';

export interface ChatSummary {
  uuid: string;
  title: string;
  study_uuid: string | null;
  started_at: string | null;
  ended_at: string | null;
}

// Shape the sidebar UI consumes. lastMessagePreview/messageCount aren't
// returned by the copilot REST API yet, so they're optional.
export interface CopilotChatSummary extends ChatSummary {
  lastMessagePreview?: string;
  messageCount?: number;
}

export interface ChatMessage {
  seq: number;
  role: 'user' | 'assistant' | 'tool';
  content: unknown;
  created_at: string | null;
}

interface ChatListResponse {
  conversations: ChatSummary[];
  count: number;
}

interface ChatMessagesResponse {
  conversation_uuid: string;
  messages: ChatMessage[];
  count: number;
}

export const CopilotChatsAPI = createApi({
  reducerPath: 'CopilotChatsAPI',
  baseQuery: fetchBaseQuery({
    baseUrl: config.COPILOT_API_URL,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('AccessToken');
      if (token) headers.set('Authorization', `Token ${token}`);
      return headers;
    },
  }),
  tagTypes: ['Chats'],
  endpoints: (builder) => ({
    listChats: builder.query<ChatListResponse, void>({
      query: () => ({ url: '/conversations', method: 'GET' }),
      providesTags: [{ type: 'Chats', id: 'LIST' }],
    }),
    getChatMessages: builder.query<
      ChatMessagesResponse,
      { uuid: string; limit?: number }
    >({
      query: ({ uuid, limit }) => ({
        url: `/conversations/${uuid}/messages`,
        method: 'GET',
        params: limit ? { limit } : undefined,
      }),
    }),
    renameChat: builder.mutation<ChatSummary, { uuid: string; title: string }>({
      query: ({ uuid, title }) => ({
        url: `/conversations/${uuid}`,
        method: 'PATCH',
        body: { title },
      }),
      invalidatesTags: [{ type: 'Chats', id: 'LIST' }],
    }),
    deleteChat: builder.mutation<void, string>({
      query: (uuid) => ({ url: `/conversations/${uuid}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'Chats', id: 'LIST' }],
    }),
  }),
});

export const {
  useListChatsQuery,
  useLazyListChatsQuery,
  useGetChatMessagesQuery,
  useLazyGetChatMessagesQuery,
  useRenameChatMutation,
  useDeleteChatMutation,
} = CopilotChatsAPI;
